import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type FormEvent } from 'react'
import { api, type Contact, type ContactInput } from '../../lib/api'

const emptyContact: ContactInput = {
  kind: 'person', name: '', legalName: '', taxId: '', email: '', phone: '', postalCode: '', street: '',
  addressNumber: '', complement: '', neighborhood: '', city: '', state: '', country: 'BR', notes: '',
}

export function ContactsPage() {
  const queryClient = useQueryClient()
  const organizationId = localStorage.getItem('cromaos.activeOrganization') ?? ''
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState('')
  const [status, setStatus] = useState('active')
  const [page, setPage] = useState(1)
  const [editing, setEditing] = useState<Contact | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState<ContactInput>(emptyContact)

  const filters = useMemo(() => ({ q: search, kind, status, page, pageSize: 20 }), [search, kind, status, page])
  const contacts = useQuery({
    queryKey: ['contacts', organizationId, filters],
    queryFn: () => api.contacts(organizationId, filters),
    enabled: Boolean(organizationId),
  })

  const save = useMutation({
    mutationFn: () => editing ? api.updateContact(organizationId, editing.id, form) : api.createContact(organizationId, form),
    onSuccess: async () => {
      closeForm()
      await queryClient.invalidateQueries({ queryKey: ['contacts', organizationId] })
    },
  })
  const setArchived = useMutation({
    mutationFn: ({ contact, archive }: { contact: Contact; archive: boolean }) => archive
      ? api.archiveContact(organizationId, contact.id)
      : api.restoreContact(organizationId, contact.id),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['contacts', organizationId] }),
  })

  function openNew() {
    setEditing(null)
    setForm(emptyContact)
    setFormOpen(true)
  }

  function openEdit(contact: Contact) {
    setEditing(contact)
    setForm({
      kind: contact.kind, name: contact.name, legalName: contact.legalName ?? '', taxId: contact.taxId ?? '',
      email: contact.email ?? '', phone: contact.phone ?? '', postalCode: contact.postalCode ?? '', street: contact.street ?? '',
      addressNumber: contact.addressNumber ?? '', complement: contact.complement ?? '', neighborhood: contact.neighborhood ?? '',
      city: contact.city ?? '', state: contact.state ?? '', country: contact.country, notes: contact.notes ?? '',
    })
    setFormOpen(true)
  }

  function closeForm() {
    setFormOpen(false)
    setEditing(null)
    setForm(emptyContact)
    save.reset()
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    save.mutate()
  }

  function update<K extends keyof ContactInput>(key: K, value: ContactInput[K]) {
    setForm(current => ({ ...current, [key]: value }))
  }

  if (!organizationId) {
    return <main className="workspace-content"><section className="empty-state"><h1>Selecione uma organização</h1><p>Volte à visão geral e escolha a empresa que deseja administrar.</p></section></main>
  }

  return (
    <main className="workspace-content contacts-page">
      <header className="page-heading page-heading-actions">
        <div><p className="eyebrow">RELACIONAMENTO</p><h1>Contatos</h1><p className="muted">Clientes, leads e empresas em um só lugar.</p></div>
        <button onClick={openNew}>Novo contato</button>
      </header>

      <section className="content-card contacts-card">
        <form className="contact-filters" onSubmit={event => { event.preventDefault(); setPage(1); setSearch(query.trim()) }}>
          <input aria-label="Buscar contatos" placeholder="Buscar por nome, documento, email ou telefone" value={query} onChange={event => setQuery(event.target.value)} />
          <select aria-label="Tipo" value={kind} onChange={event => { setKind(event.target.value); setPage(1) }}><option value="">Todos os tipos</option><option value="person">Pessoa</option><option value="company">Empresa</option></select>
          <select aria-label="Status" value={status} onChange={event => { setStatus(event.target.value); setPage(1) }}><option value="active">Ativos</option><option value="archived">Arquivados</option><option value="">Todos</option></select>
          <button className="secondary" type="submit">Buscar</button>
        </form>

        {contacts.isPending && <p className="muted">Carregando contatos…</p>}
        {contacts.isError && <p className="error">{contacts.error.message}</p>}
        {setArchived.isError && <p className="error">{setArchived.error.message}</p>}
        {contacts.data?.items.length === 0 && <div className="empty-state compact"><h2>Nenhum contato encontrado</h2><p>Cadastre o primeiro contato ou ajuste os filtros.</p></div>}
        {contacts.data && contacts.data.items.length > 0 && (
          <div className="contact-table-wrap"><table className="contact-table"><thead><tr><th>Contato</th><th>Tipo</th><th>Documento</th><th>Cidade</th><th>Status</th><th><span className="sr-only">Ações</span></th></tr></thead><tbody>
            {contacts.data.items.map(contact => <tr key={contact.id}>
              <td><strong>{contact.name}</strong><span>{contact.email || contact.phone || 'Sem contato informado'}</span></td>
              <td>{contact.kind === 'person' ? 'Pessoa' : 'Empresa'}</td><td>{contact.taxId || '—'}</td><td>{contact.city || '—'}{contact.state ? ` / ${contact.state}` : ''}</td>
              <td><span className={`status-pill ${contact.status}`}>{contact.status === 'active' ? 'Ativo' : 'Arquivado'}</span></td>
              <td className="table-actions"><button className="table-button" onClick={() => openEdit(contact)}>Editar</button><button className="table-button danger" disabled={setArchived.isPending} onClick={() => setArchived.mutate({ contact, archive: contact.status === 'active' })}>{contact.status === 'active' ? 'Arquivar' : 'Restaurar'}</button></td>
            </tr>)}
          </tbody></table></div>
        )}
        {contacts.data && contacts.data.totalPages > 1 && <footer className="pagination"><button className="secondary" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>Anterior</button><span>Página {contacts.data.page} de {contacts.data.totalPages} · {contacts.data.total} contatos</span><button className="secondary" disabled={page >= contacts.data.totalPages} onClick={() => setPage(value => value + 1)}>Próxima</button></footer>}
      </section>

      {formOpen && <div className="form-overlay" role="presentation"><section className="contact-form-panel" role="dialog" aria-modal="true" aria-labelledby="contact-form-title">
        <header><div><p className="eyebrow">{editing ? 'EDITAR' : 'NOVO'}</p><h2 id="contact-form-title">{editing ? editing.name : 'Novo contato'}</h2></div><button className="close-button" type="button" onClick={closeForm} aria-label="Fechar">×</button></header>
        <form onSubmit={submit} className="contact-form">
          <div className="form-grid"><label>Tipo<select value={form.kind} onChange={event => update('kind', event.target.value as ContactInput['kind'])}><option value="person">Pessoa</option><option value="company">Empresa</option></select></label><label>Nome ou nome fantasia<input value={form.name} onChange={event => update('name', event.target.value)} minLength={2} maxLength={160} required /></label></div>
          <div className="form-grid"><label>Razão social<input value={form.legalName} onChange={event => update('legalName', event.target.value)} /></label><label>CPF ou CNPJ<input value={form.taxId} onChange={event => update('taxId', event.target.value)} maxLength={32} /></label></div>
          <div className="form-grid"><label>Email<input type="email" value={form.email} onChange={event => update('email', event.target.value)} /></label><label>Telefone<input value={form.phone} onChange={event => update('phone', event.target.value)} maxLength={40} /></label></div>
          <h3>Endereço</h3>
          <div className="form-grid three"><label>CEP<input value={form.postalCode} onChange={event => update('postalCode', event.target.value)} /></label><label className="span-two">Logradouro<input value={form.street} onChange={event => update('street', event.target.value)} /></label></div>
          <div className="form-grid three"><label>Número<input value={form.addressNumber} onChange={event => update('addressNumber', event.target.value)} /></label><label>Complemento<input value={form.complement} onChange={event => update('complement', event.target.value)} /></label><label>Bairro<input value={form.neighborhood} onChange={event => update('neighborhood', event.target.value)} /></label></div>
          <div className="form-grid three"><label className="span-two">Cidade<input value={form.city} onChange={event => update('city', event.target.value)} /></label><label>UF<input value={form.state} onChange={event => update('state', event.target.value.toUpperCase())} maxLength={2} /></label></div>
          <label>Observações<textarea value={form.notes} onChange={event => update('notes', event.target.value)} maxLength={4000} rows={4} /></label>
          {save.isError && <p className="error">{save.error.message}</p>}
          <footer><button className="secondary" type="button" onClick={closeForm}>Cancelar</button><button disabled={save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar contato'}</button></footer>
        </form>
      </section></div>}
    </main>
  )
}
